/**
 * Hotels page main script
 * Handles hotel search functionality, results display, and filtering
 */

import { HeaderComponent } from './components/HeaderComponent';
import { HotelSearchComponent } from './components/HotelSearchComponent';
import { HotelResultsComponent } from './components/HotelResultsComponent';
import { HotelFiltersComponent } from './components/HotelFiltersComponent';
import { HotelSearchData, HotelResult, HotelFilters } from './types/data-models';

class HotelsPage {
  private searchComponent: HotelSearchComponent | null = null;
  private resultsComponent: HotelResultsComponent | null = null;
  private filtersComponent: HotelFiltersComponent | null = null;
  private currentSearchData: HotelSearchData | null = null;
  private currentFilters: HotelFilters = {};

  constructor() {
    this.init();
  }

  private init(): void {
    this.initializeComponents();
    this.bindEvents();
    this.handleInitialSearch();
  }

  private initializeComponents(): void {
    // Initialize header component
    new HeaderComponent();

    // Initialize search form component
    const searchForm = document.getElementById('hotel-search-form') as HTMLFormElement;
    if (searchForm) {
      this.searchComponent = new HotelSearchComponent(searchForm);
    }

    // Initialize results component
    const resultsContainer = document.getElementById('hotel-results');
    if (resultsContainer) {
      this.resultsComponent = new HotelResultsComponent(resultsContainer);
    }

    // Initialize filters component
    const filtersContainer = document.querySelector('.search-filters');
    if (filtersContainer) {
      this.filtersComponent = new HotelFiltersComponent(filtersContainer as HTMLElement);
    }
  }

  private bindEvents(): void {
    // Search form submission
    if (this.searchComponent) {
      this.searchComponent.onSearch = (searchData: HotelSearchData) => {
        this.handleSearch(searchData);
      };
    }

    // Filter changes
    if (this.filtersComponent) {
      this.filtersComponent.onFilterChange = (filters: HotelFilters) => {
        this.currentFilters = filters;
        this.applyFilters();
      };

      this.filtersComponent.onClearFilters = () => {
        this.currentFilters = {};
        this.applyFilters();
      };
    }

    // Results interactions
    if (this.resultsComponent) {
      this.resultsComponent.onHotelSelect = (hotelId: string) => {
        this.handleHotelSelection(hotelId);
      };

      this.resultsComponent.onSortChange = (sortBy: string) => {
        this.handleSortChange(sortBy);
      };

      this.resultsComponent.onPageChange = (page: number) => {
        this.handlePageChange(page);
      };
    }

    // Retry search button
    const retryButton = document.querySelector('.no-results__retry');
    if (retryButton) {
      retryButton.addEventListener('click', () => {
        this.showSearchForm();
      });
    }

    // Map toggle button (placeholder functionality)
    this.bindMapToggle();
  }

  private bindMapToggle(): void {
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('hotel-card__map')) {
        this.toggleMapView();
      }
    });
  }

  private toggleMapView(): void {
    const mapContainer = document.getElementById('map-container');
    if (mapContainer) {
      const isVisible = mapContainer.style.display !== 'none';
      mapContainer.style.display = isVisible ? 'none' : 'block';
      
      if (!isVisible) {
        mapContainer.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }

  private handleInitialSearch(): void {
    // Check if there are URL parameters for initial search
    const urlParams = new URLSearchParams(window.location.search);
    const destination = urlParams.get('destination');
    const checkinDate = urlParams.get('checkinDate');
    const checkoutDate = urlParams.get('checkoutDate');
    const guests = urlParams.get('guests');
    const rooms = urlParams.get('rooms');

    if (destination && checkinDate && checkoutDate) {
      const searchData: HotelSearchData = {
        destination,
        checkinDate: new Date(checkinDate),
        checkoutDate: new Date(checkoutDate),
        guests: guests ? parseInt(guests) : 2,
        rooms: rooms ? parseInt(rooms) : 1
      };

      // Populate form with initial data
      if (this.searchComponent) {
        this.searchComponent.populateForm(searchData);
      }

      // Perform initial search
      this.handleSearch(searchData);
    }
  }

  private async handleSearch(searchData: HotelSearchData): Promise<void> {
    this.currentSearchData = searchData;
    this.showLoadingState();

    try {
      // Simulate API call - in real implementation, this would call actual hotel search API
      const results = await this.searchHotels(searchData);
      
      if (results.length > 0) {
        this.showResults(results);
        this.updateFiltersWithResults(results);
      } else {
        this.showNoResults();
      }
    } catch (error) {
      console.error('Hotel search error:', error);
      this.showError('Une erreur est survenue lors de la recherche. Veuillez réessayer.');
    }
  }

  private async searchHotels(searchData: HotelSearchData): Promise<HotelResult[]> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Mock hotel data - in real implementation, this would come from API
    return this.generateMockHotels(searchData);
  }

  private generateMockHotels(searchData: HotelSearchData): HotelResult[] {
    const hotelNames = [
      'Hôtel des Jardins', 'Le Grand Palace', 'Villa Moderne', 'Auberge du Centre',
      'Résidence Élégante', 'Hôtel Boutique', 'Le Petit Château', 'Maison d\'Hôtes',
      'Hôtel de Luxe', 'Pension Familiale', 'Resort & Spa', 'Hôtel Économique',
      'Palace Royal', 'Hôtel Design', 'Auberge Traditionnelle'
    ];

    const amenitiesList = [
      ['WiFi gratuit', 'Parking', 'Climatisation', 'Room service'],
      ['Piscine', 'Spa', 'Restaurant', 'Bar', 'WiFi gratuit'],
      ['Salle de sport', 'WiFi gratuit', 'Parking', 'Petit-déjeuner inclus'],
      ['Restaurant', 'Bar', 'WiFi gratuit', 'Climatisation'],
      ['Spa', 'Piscine', 'Salle de sport', 'WiFi gratuit', 'Parking'],
      ['WiFi gratuit', 'Petit-déjeuner inclus', 'Climatisation'],
      ['Piscine', 'Restaurant', 'WiFi gratuit', 'Parking', 'Bar'],
      ['WiFi gratuit', 'Parking', 'Petit-déjeuner inclus', 'Room service']
    ];

    const hotels: HotelResult[] = [];
    const basePrice = 80;

    for (let i = 0; i < 20; i++) {
      const nameIndex = i % hotelNames.length;
      const amenitiesIndex = i % amenitiesList.length;
      const selectedName = hotelNames[nameIndex];
      const selectedAmenities = amenitiesList[amenitiesIndex];
      
      if (!selectedName || !selectedAmenities) continue;

      const rating = 2 + Math.random() * 3; // 2-5 stars
      const reviewCount = 50 + Math.floor(Math.random() * 500);
      const price = basePrice + (Math.random() * 200) + (rating * 20);
      const hasBreakfast = Math.random() > 0.5;

      hotels.push({
        id: `hotel-${i + 1}`,
        name: `${selectedName} ${searchData.destination}`,
        address: `${10 + i} Rue de l'Exemple, ${searchData.destination}`,
        rating: Math.round(rating * 10) / 10,
        reviewCount,
        price: {
          min: Math.floor(price),
          max: Math.floor(price * 1.3),
          currency: 'EUR'
        },
        amenities: selectedAmenities,
        images: [
          `../assets/images/hotels/hotel-${i + 1}-1.jpg`,
          `../assets/images/hotels/hotel-${i + 1}-2.jpg`,
          `../assets/images/hotels/hotel-${i + 1}-3.jpg`
        ],
        description: `Un établissement ${rating >= 4 ? 'de luxe' : rating >= 3 ? 'confortable' : 'économique'} situé au cœur de ${searchData.destination}. Idéal pour un séjour ${searchData.guests > 2 ? 'en famille' : 'en couple'}.`,
        coordinates: {
          lat: 48.8566 + (Math.random() - 0.5) * 0.1,
          lng: 2.3522 + (Math.random() - 0.5) * 0.1
        },
        distance: `${(Math.random() * 5).toFixed(1)} km`,
        cancellation: Math.random() > 0.3 ? 'Annulation gratuite' : 'Non remboursable',
        breakfast: hasBreakfast
      });
    }

    return hotels.sort((a, b) => a.price.min - b.price.min);
  }

  private showLoadingState(): void {
    const searchResults = document.getElementById('search-results');
    const loadingState = document.getElementById('loading-state');
    const hotelResults = document.getElementById('hotel-results');
    const noResults = document.getElementById('no-results');
    const mapContainer = document.getElementById('map-container');

    if (searchResults) searchResults.style.display = 'block';
    if (loadingState) loadingState.style.display = 'block';
    if (hotelResults) hotelResults.style.display = 'none';
    if (noResults) noResults.style.display = 'none';
    if (mapContainer) mapContainer.style.display = 'none';
  }

  private showResults(hotels: HotelResult[]): void {
    const loadingState = document.getElementById('loading-state');
    const hotelResults = document.getElementById('hotel-results');
    const noResults = document.getElementById('no-results');
    const resultsSummary = document.getElementById('results-summary');

    if (loadingState) loadingState.style.display = 'none';
    if (hotelResults) hotelResults.style.display = 'block';
    if (noResults) noResults.style.display = 'none';

    if (resultsSummary) {
      resultsSummary.textContent = `${hotels.length} hôtel${hotels.length > 1 ? 's' : ''} trouvé${hotels.length > 1 ? 's' : ''}`;
    }

    if (this.resultsComponent) {
      this.resultsComponent.displayHotels(hotels);
    }
  }

  private showNoResults(): void {
    const loadingState = document.getElementById('loading-state');
    const hotelResults = document.getElementById('hotel-results');
    const noResults = document.getElementById('no-results');
    const mapContainer = document.getElementById('map-container');

    if (loadingState) loadingState.style.display = 'none';
    if (hotelResults) hotelResults.style.display = 'none';
    if (noResults) noResults.style.display = 'block';
    if (mapContainer) mapContainer.style.display = 'none';
  }

  private showError(message: string): void {
    const loadingState = document.getElementById('loading-state');
    const hotelResults = document.getElementById('hotel-results');
    const noResults = document.getElementById('no-results');

    if (loadingState) loadingState.style.display = 'none';
    if (hotelResults) hotelResults.style.display = 'none';
    if (noResults) {
      noResults.style.display = 'block';
      const errorMessage = noResults.querySelector('.no-results__message');
      if (errorMessage) {
        errorMessage.textContent = message;
      }
    }
  }

  private showSearchForm(): void {
    const searchResults = document.getElementById('search-results');
    if (searchResults) {
      searchResults.style.display = 'none';
    }
    
    // Scroll to search form
    const searchSection = document.querySelector('.search-section');
    if (searchSection) {
      searchSection.scrollIntoView({ behavior: 'smooth' });
    }
  }

  private updateFiltersWithResults(hotels: HotelResult[]): void {
    if (this.filtersComponent) {
      this.filtersComponent.updateAvailableFilters(hotels);
    }
  }

  private applyFilters(): void {
    if (this.resultsComponent && this.currentSearchData) {
      this.resultsComponent.applyFilters(this.currentFilters);
      
      // Update results summary
      const resultsSummary = document.getElementById('results-summary');
      if (resultsSummary) {
        const filteredCount = this.resultsComponent.getFilteredHotelsCount();
        resultsSummary.textContent = `${filteredCount} hôtel${filteredCount > 1 ? 's' : ''} trouvé${filteredCount > 1 ? 's' : ''}`;
      }
    }

    if (this.filtersComponent) {
      this.filtersComponent.updateClearButtonState();
    }
  }

  private handleHotelSelection(hotelId: string): void {
    // In a real application, this would navigate to hotel details page
    console.log('Hotel selected:', hotelId);
    alert(`Hôtel sélectionné: ${hotelId}. Redirection vers la page de détails...`);
  }

  private handleSortChange(sortBy: string): void {
    if (this.resultsComponent) {
      this.resultsComponent.sortResults(sortBy);
    }
  }

  private handlePageChange(page: number): void {
    if (this.resultsComponent) {
      this.resultsComponent.goToPage(page);
    }
  }
}

// Initialize the hotels page when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new HotelsPage();
});